import { ajaxStart, ajaxEnd } from '../context/ajax.js';
import { openAlert } from '../context/alert.js';
import { setTokenExpired } from '../context/token.js';
import { setLoginSucceeded } from '../context/loginSucceeded.js';
import { setEmailAddress } from '../context/emailAddress.js';
import { setIsSuperAdmin } from '../context/isSuperAdmin.js';
import { setMustChangePassword } from '../context/mustChangePassword.js';

const PUBLIC_ENDPOINTS = new Set([
	'login',
	'signup',
	'verify',
	'acceptInvite',
	'resetPassword',
	'resetPasswordStart',
	'resendVerification'
]);

export default class Utils {
	static authBlocked = false;
	static activeXhrs = new Set();

	static isOnline() {
		return navigator.onLine;
	}

	static getApiOrigin() {
		return location.origin;
	}

	static allowAuth() {
		Utils.authBlocked = false;
	}

	static abortAllAjax() {
		Utils.activeXhrs.forEach(xhr => {
			try {
				xhr._abortedForAuth = true;
				xhr.abort();
			} catch (e) {
				// ignore abort failures
			}
		});
		Utils.activeXhrs.clear();
	}

	static clearAuthSession(dispatch) {
		localStorage.removeItem('token');
		Utils.authBlocked = true;
		Utils.abortAllAjax();
		if (typeof dispatch === 'function') {
			dispatch(setTokenExpired(true));
			dispatch(setLoginSucceeded(false));
			dispatch(setEmailAddress(''));
			dispatch(setIsSuperAdmin(false));
			dispatch(setMustChangePassword(false));
		}
	}

	/** Fire-and-forget server revoke using a captured token (survives local clear + abort). */
	static revokeServerToken(token) {
		if (!token || !Utils.isOnline()) {
			return;
		}
		const origin = Utils.getApiOrigin();
		const xhr = new XMLHttpRequest();
		xhr.open('POST', `${origin}/server/logout.php`, true);
		xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
		xhr.send(
			`Token=${encodeURIComponent(token)}&clientVersion=2`
		);
	}

	/**
	 * Promise-based authenticated POST to /server/{url}.php.
	 * Resolves with the parsed JSON body for both success and application errors.
	 * Rejects on network / parse failure.
	 *
	 * @param {string} url Endpoint name without .php
	 * @param {Object|FormData} data Payload
	 * @param {Function} [dispatch] Store dispatch for loading overlay / optional alerts
	 * @param {{ suppressErrorAlert?: boolean }} [options]
	 * @returns {Promise<Object>}
	 */
	static ajaxAsync(url, data, dispatch, options = {}) {
		const { suppressErrorAlert = false } = options;
		return new Promise((resolve, reject) => {
			const isPublic = PUBLIC_ENDPOINTS.has(url);
			if (Utils.authBlocked && !isPublic) {
				reject(new Error('Authentication blocked'));
				return;
			}
			if (!this.isOnline()) {
				if (typeof dispatch === 'function' && !suppressErrorAlert) {
					dispatch(openAlert('Offline Mode', "Oops, this function doesn't support offline mode. Please try again when online."));
				}
				reject(new Error('Offline'));
				return;
			}

			const clientVersion = '2';
			const origin = this.getApiOrigin();
			const xhr = new XMLHttpRequest();
			const token = (url === 'verify' || url === 'acceptInvite') ? null : localStorage.getItem('token');
			const ajaxRequestId = `${url}_${Math.random()}_${Date.now()}`;

			if (typeof dispatch === 'function') {
				dispatch(ajaxStart(ajaxRequestId));
			}

			xhr.open('POST', `${origin}/server/${url}.php`, true);

			let body = data;
			if (Object.prototype.toString.call(data) === '[object FormData]') {
				if (token) data.append('Token', token);
				data.append('clientVersion', clientVersion);
			} else {
				xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
				const payload = { ...data, clientVersion };
				if (token) payload.Token = token;
				body = Object.keys(payload).map(key => {
					const value = payload[key] == null ? '' : String(payload[key]);
					return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
				}).join('&');
			}

			Utils.activeXhrs.add(xhr);

			if (import.meta.env.DEV) {
				xhr.timeout = 2000;
			}

			xhr.ontimeout = function () {
				Utils.activeXhrs.delete(xhr);
				if (typeof dispatch === 'function') {
					dispatch(ajaxEnd(ajaxRequestId));
				}
				reject(new Error('Request timed out'));
			};

			xhr.onreadystatechange = function () {
				if (xhr.readyState !== 4) return;
				Utils.activeXhrs.delete(xhr);
				if (typeof dispatch === 'function') {
					dispatch(ajaxEnd(ajaxRequestId));
				}
				if (xhr._abortedForAuth) {
					reject(new Error('Aborted for auth'));
					return;
				}
				if (xhr.status !== 200) {
					const networkError = xhr.status === 0
						? 'The request was aborted or could not reach the server.'
						: (xhr.statusText || `Request failed with status ${xhr.status}`);
					if (typeof dispatch === 'function' && !suppressErrorAlert) {
						dispatch(openAlert('Error', networkError));
					}
					reject(new Error(networkError));
					return;
				}
				const result = Utils.safeJSONParse(xhr.responseText);
				if (!result) {
					if (typeof dispatch === 'function' && !suppressErrorAlert) {
						dispatch(openAlert('Server Error', 'A server error occurred, please try again later.'));
					}
					reject(new Error('Invalid JSON'));
					return;
				}
				if (result.reauthenticate) {
					Utils.clearAuthSession(dispatch);
					if (typeof dispatch === 'function' && !suppressErrorAlert) {
						dispatch(openAlert('Error', `An error that requires reauthentication occured. ${result.error ? 'Error: ' + result.error : ''}`));
					}
					reject(Object.assign(new Error(result.error || 'Reauthenticate'), { result }));
					return;
				}
				if (!result.success && typeof dispatch === 'function' && !suppressErrorAlert) {
					dispatch(openAlert('Error', `A service [${url}] error occured. ${result.error ? 'Error: ' + result.error : ''}`));
				}
				resolve(result);
			};
			xhr.send(body);
		});
	}

	static ajax(url, data, dispatch, callback, options = {}) {
		const isPublic = PUBLIC_ENDPOINTS.has(url);
		if (Utils.authBlocked && !isPublic) {
			if (typeof options.onAuthBlocked === 'function') {
				options.onAuthBlocked();
			}
			return;
		}

		if (!this.isOnline()) {
			if (typeof dispatch === 'function') {
				dispatch(openAlert('Offline Mode', "Oops, this function doesn't support offline mode. Please try again when online."));
			}
			return;
		}
		const clientVersion = '2',
			origin = this.getApiOrigin(),
			xhr = new XMLHttpRequest(),
			token = (url === 'verify' || url === 'acceptInvite') ? null : localStorage.getItem('token'),
			ajaxRequestId = `${url}_${Math.random()}_${new Date().getTime()}`;
		if (typeof dispatch === 'function') {
			dispatch(ajaxStart(ajaxRequestId));
		}

		xhr.open('POST', `${origin}/server/${url}.php`, true);

		if (Object.prototype.toString.call(data) === '[object FormData]') {
			if (token) data.append('Token', token);
			data.append('clientVersion', clientVersion);
		} else {
			xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
			if (token) data.Token = token;
			data.clientVersion = clientVersion;
			data = Object.keys(data).map(key => {
				const value = data[key] == null ? '' : String(data[key]);
				return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
			}).join('&');
		}

		Utils.activeXhrs.add(xhr);

		if (import.meta.env.DEV) {
			xhr.timeout = 2000;
			xhr.ontimeout = function () {
				Utils.activeXhrs.delete(xhr);
				if (typeof dispatch === 'function') {
					dispatch(ajaxEnd(ajaxRequestId));
				}
			};
		}

		xhr.onreadystatechange = function () {
			if (xhr.readyState === 4) {
				Utils.activeXhrs.delete(xhr);
				// Always clear loading overlay for completed requests (including aborts / non-200).
				if (typeof dispatch === 'function') {
					dispatch(ajaxEnd(ajaxRequestId));
				}
				if (xhr._abortedForAuth) {
					return;
				}
				if (xhr.status === 200) {
					const result = Utils.safeJSONParse(xhr.responseText);
					if (!result) {
						if (typeof dispatch === 'function') {
							if (typeof xhr.responseText === 'object' && xhr.responseText.constructor.name === 'XMLHttpRequest') {
								dispatch(openAlert('Server Error', 'A server error occurred, please try again later. Error was: ' + xhr.responseText.statusText));
							} else {
								dispatch(openAlert('Server Error', 'A server error occurred, please try again later.'));
							}
						}
					} else if (result.reauthenticate) {
						Utils.clearAuthSession(dispatch);
						if (typeof options.onReauthenticate === 'function') {
							options.onReauthenticate(result);
						}
						if (typeof dispatch === 'function') {
							dispatch(openAlert('Error', `An error that requires reauthentication occured. ${result.error ? 'Error: ' + result.error : ''}`, [{
								text: 'OK',
								action: () => true
							}]));
						}
					} else if (!result.success) {
						if (typeof dispatch === 'function') {
							dispatch(openAlert('Error', `A service [${url}] error occured. ${result.error ? 'Error: ' + result.error : ''}`));
						}
					} else {
						if (typeof callback === 'function') {
							callback(result);
						}
					}
				} else {
					const networkError = xhr.status === 0
						? 'The request was aborted or could not reach the server. Please try again.'
						: (xhr.statusText || `Request failed with status ${xhr.status}`);
					if (typeof dispatch === 'function') {
						dispatch(openAlert('Error', networkError));
					}
				}
			}
		};
		xhr.send(data);
	}

	static makeSafeForJS(name) {
		return name.replace(/[^a-z0-9]/g, function(s) {
			const c = s.charCodeAt(0);
			if (c === 32) return '-';
			if (c >= 65 && c <= 90) return '_' + s.toLowerCase();
			return '__' + ('000' + c.toString(16)).slice(-4);
		});
	}

	static getCookie(name) {
		const value = '; ' + document.cookie;
		const parts = value.split('; ' + name + '=');
		if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
		return false;
	}

	static deleteCookie(name, path, domain) {
		if (this.getCookie(name)) {
			document.cookie = name + '=' +
				((path) ? ';path=' + path : '') +
				((domain) ? ';domain=' + domain : '') +
				';expires=Thu, 01 Jan 1970 00:00:01 GMT';
		}
	}

	static parseQuery(qstr) {
		const query = {};
		const a = (qstr[0] === '?' ? qstr.substr(1) : qstr).split('&');
		for (let i = 0; i < a.length; i++) {
			const b = a[i].split('=');
			query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || '');
		}
		return query;
	}

	static debounce(func, wait, immediate) { //https://gist.github.com/nmsdvid/8807205
		let timeout;
		return function() {
			const context = this,
				args = arguments;
			clearTimeout(timeout);
			timeout = setTimeout(function() {
				timeout = null;
				if (!immediate) func.apply(context, args);
			}, wait);
			if (immediate && !timeout) func.apply(context, args);
		};
	}

	static safeJSONParse(str) {
		if (typeof str !== 'string') return false;
		let json;

		try {
			json = JSON.parse(str);
		} catch (e) {
			return false;
		}

		return typeof json === 'object' && json !== null ? json : false;
	}
}
